import { powderyMildewRiskIndexLevelMap } from '@semios/app-platform-common'
import { getTimezoneForSelectedPropertyOrRegion } from 'App/Map/PanelDetails/_utils/getTimezoneForSelectedPropertyOrRegion'
import type { TXAxisPlotLinesOptionsWithTooltipDisplay } from 'components/StackedChart/_utils/lineChartTooltipFormatter/buildTooltipPlotLinesIfAny'
import type { HighchartsOptionsWithSemiosAdditions, TChartSeries } from 'components/StackedChart/types'
import type { AxisTitleAlignValue } from 'highcharts'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { alphaColorReplacer, colors, type TRGBAColorWith1AtTheEnd } from 'settings/colors'
import { detailsPanelStore } from 'stores/detailsPanelStore/detailsPanelStore'
import { getScreenSize } from 'utils/getScreenSize'
import { getXDateFormat } from './getXDateFormat'
import { makeCompareSeasonsSeriesFromRegularSeries } from './makeCompareSeasonsSeriesFromRegularSeries'

export type TPowderyMildewPlotLine = {
  value: number
  label: string
  id: string
  isCompareSeasons?: boolean
}

export const secondaryInfectionRiskChartHelper = ({
  data,
  compareSeasonsData,
  compareSeasonsInterval,
  forecastStartsAt,
  seriesName,
  seriesId,
  seriesTooltip,
  plotLines,
}: {
  data: Array<[number, number | null]>
  compareSeasonsData?: Array<[number, number | null]>
  compareSeasonsInterval?: number
  forecastStartsAt: number
  seriesName: string
  seriesId: string
  seriesTooltip: TChartSeries['tooltip']
  plotLines?: TPowderyMildewPlotLine[]
}): HighchartsOptionsWithSemiosAdditions => {
  const { isWideScreen } = getScreenSize()
  const series: TChartSeries[] = []
  const { dateFrom, dateTo } = detailsPanelStore.getState()
  const timezone = getTimezoneForSelectedPropertyOrRegion()
  // Only fill in gaps in the data array, assuming data is sorted by timestamp ascending

  const powderyMildewSeries: TChartSeries & { color?: TRGBAColorWith1AtTheEnd } = {
    name: seriesName,
    id: seriesId,
    tooltip: seriesTooltip,
    data: processData(data),
    type: 'line',
    marker: {
      enabled: false,
    },
    color: colors.midnight,
    // keep the series active even if hovering over plot lines
    states: {
      inactive: {
        enabled: false,
      },
    },
    connectNulls: false,
  }

  series.push(powderyMildewSeries)

  if (compareSeasonsInterval && compareSeasonsData) {
    series.push(
      makeCompareSeasonsSeriesFromRegularSeries(powderyMildewSeries, {
        data: processData(compareSeasonsData),
      }),
    )
  }

  const plotBands =
    data.length || compareSeasonsData?.length
      ? [
          {
            // Low risk
            id: 'lowRisk',
            legendLabel: translate.phrases.banyanApp(powderyMildewRiskIndexLevelMap.LOW.label),
            from: powderyMildewRiskIndexLevelMap.LOW.min,
            to: powderyMildewRiskIndexLevelMap.LOW.max,
            color: powderyMildewRiskIndexLevelMap.LOW.color,
          },
          {
            // Moderate risk
            id: 'moderateRisk',
            legendLabel: translate.phrases.banyanApp(powderyMildewRiskIndexLevelMap.MODERATE.label),
            from: powderyMildewRiskIndexLevelMap.MODERATE.min,
            to: powderyMildewRiskIndexLevelMap.MODERATE.max,
            color: powderyMildewRiskIndexLevelMap.MODERATE.color,
          },
          {
            // High risk
            id: 'highRisk',
            legendLabel: translate.phrases.banyanApp(powderyMildewRiskIndexLevelMap.HIGH.label),
            from: powderyMildewRiskIndexLevelMap.HIGH.min,
            to: powderyMildewRiskIndexLevelMap.HIGH.max,
            color: powderyMildewRiskIndexLevelMap.HIGH.color,
          },
        ]
      : []

  const emptySeriesForPlotBandsInLegend: TChartSeries[] = plotBands.reverse().map((band) => ({
    type: 'column',
    id: band.id,
    name: band.legendLabel,
    color: band.color,
    data: [],
    showInLegend: true,
    hideFromTooltip: true,
  }))

  series.push(...emptySeriesForPlotBandsInLegend)

  const XAxisPlotLines: TXAxisPlotLinesOptionsWithTooltipDisplay[] =
    plotLines?.map((line) => ({
      id: line.id,
      value: line.value,
      color: line.isCompareSeasons ? alphaColorReplacer(colors.midnight, 0.4) : colors.midnight,
      width: 2,
      dashStyle: 'ShortDot',
      zIndex: 2,
      tooltipDisplay: (ctx) =>
        compareSeasonsInterval
          ? translate.phrases.templates('{{labelA}} ({{labelB}})', {
              labelA: line.label,
              labelB: translate.dates.format(
                moment
                  .tz(ctx.x, timezone)
                  .subtract(line.isCompareSeasons ? compareSeasonsInterval : 0, 'year'),
                'YYYY',
              ),
            })
          : line.label,
    })) ?? []

  if (plotLines?.length) {
    const plotLineData = plotLines
      .filter(
        (line) =>
          !line.isCompareSeasons && moment.tz(line.value, timezone).isBetween(dateFrom, dateTo, 'day', '[]'),
      )
      .map((line) => [line.value, 0])

    if (plotLineData.length) {
      series.push({
        id: 'plotLines',
        data: plotLineData,
        type: 'line',
        color: 'transparent',
        showInLegend: false,
        hideFromTooltip: true,
        connectNulls: false,
        states: {
          inactive: {
            enabled: false,
          },
        },
      })
    }
  }

  return {
    semiosHighchartsAdditions: {
      id: seriesId,
      firstForecastTimestamp: forecastStartsAt,
    },
    chart: {
      type: 'line',
      marginTop: 20,
      // height: 350,
    },
    legend: {
      symbolRadius: 2, // rounded-square legends for risks
      // on small screens, this will make the items wrap to multiple lines
      layout: 'horizontal',
      alignColumns: false,
    },
    tooltip: {
      xDateFormat: getXDateFormat(true),
    },
    xAxis: {
      plotLines: XAxisPlotLines,
    },
    yAxis: {
      labels: {
        style: {
          fontSize: '14px',
        },
      },
      title: {
        text: translate.phrases.banyanApp('Risk Index'),
        offset: -50,
        rotation: 0,
        y: -20,
        align: 'high' as AxisTitleAlignValue,
      },
      opposite: false,
      max: 100,
      tickInterval: 25,
      endOnTick: true,
      gridLineColor: 'transparent',
      plotBands,
    },
    series,
    lang: {
      noData: translate.phrases.banyanApp(
        'Once primary infection date is reached, the secondary infection requires 3 consecutive days of certain temperatures.<br>There is no secondary infection forecasted in the next 7 days.',
      ),
    },
    noData: {
      position: {
        align: 'center',
      },
      style: {
        whiteSpace: 'unset',
        textAlign: 'center',
        marginLeft: 50,
        width: isWideScreen ? 500 : 300,
      },
    },
  }
}

// This function fills in gaps in the data array with null values for missing days
// This will ensure that nulls are displayed as empty rather than highcharts interpolating the connection
// For a year of data, it only takes 0.20ms to process
const processData = (data: Array<[number, number | null]>) => {
  const dataWithNullsFilled: Array<[number, number | null]> = []

  if (data.length === 0) {
    // nothing to do
  } else {
    let prevTimestamp = data[0][0]

    dataWithNullsFilled.push([prevTimestamp, data[0][1]])

    for (let i = 1; i < data.length; i++) {
      const [currTimestamp, currValue] = data[i]
      // Calculate the number of days between prevTimestamp and currTimestamp
      const daysDiff = Math.round((currTimestamp - prevTimestamp) / 86400000)

      // Fill in missing days (if any)
      for (let d = 1; d < daysDiff; d++) {
        const missingTimestamp = prevTimestamp + d * 86400000

        dataWithNullsFilled.push([missingTimestamp, null])
      }

      dataWithNullsFilled.push([currTimestamp, currValue])

      prevTimestamp = currTimestamp
    }
  }

  return dataWithNullsFilled
}
